import { CircleAlert, Phone, RotateCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Gmaps from '@/assets/smartcity/gmps-icon.svg';
import AudioPlayerCard from '@/domains/traffic-monitoring/components/AudioPlayerCard/AudioPlayerCard';
import BackLink from '@/domains/traffic-monitoring/components/BackLink/BackLink';
import BubbleEvent from '@/domains/traffic-monitoring/components/BubbleEvent/BubbleEvent';
import ButtonWhatsapp from '@/domains/traffic-monitoring/components/ButtonWhatsapp/ButtonWhatsapp';
import ChatBubble from '@/domains/traffic-monitoring/components/ChatBubble/ChatBubble';
import EventSwiper from '@/domains/traffic-monitoring/components/EventSwiper/EventSwiper';
import ImageGallery from '@/domains/traffic-monitoring/components/ImageGallery/ImageGallery';
import ModalConfirmation from '@/domains/traffic-monitoring/components/ModalConfirmation/ModalConfirmation';
import ModalRegister from '@/domains/traffic-monitoring/components/ModalRegister/ModalRegister';
import ModalSupport from '@/domains/traffic-monitoring/components/ModalSupport/ModalSupport';
import {
  type EventTypes,
  ListEventTypes,
} from '@/domains/traffic-monitoring/constants/listEvent.constant';
import { supportContacts } from '@/domains/traffic-monitoring/constants/supportContacts.constant';
import { useReportChat } from '@/domains/traffic-monitoring/hooks/useReportChat';
import { useReportRegistration } from '@/domains/traffic-monitoring/hooks/useReportRegistration';
import { cn } from '@/lib/utils';
import Header from '@/shared/components/layout/Header/Header';
import Button from '@/shared/components/ui/Button';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import { formatDateToDDMonYYYY, formatTimeOnly } from '@/shared/utils/date';
import { toWhatsAppNumber } from '@/shared/utils/phone';
import { showToast } from '@/shared/utils/showToast';
import { useColorStore } from '@/store/colorStore';

const ReportRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportIdParam = searchParams.get('reportId');
  const parsedReportId = reportIdParam ? Number(reportIdParam) : NaN;
  const reportId = Number.isFinite(parsedReportId) ? parsedReportId : undefined;

  const {
    report,
    status,
    images,
    incomingCalls,
    voiceNotes,
    isLoading,
    isRefreshing,
    refetch,
    decide,
    isDeciding,
    requestLocation,
    isRequestingLocation,
    refetchOnLiveMessage,
  } = useReportRegistration(reportId);

  const {
    chats,
    isFetching: isChatRefreshing,
    refetch: refetchChat,
  } = useReportChat(reportId, report?.report_code_id ?? undefined, refetchOnLiveMessage);

  const [playing, setPlaying] = useState<number | null>(null);
  const [playingIncoming, setPlayingIncoming] = useState<number | null>(null);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalRegis, setModalRegis] = useState(false);

  const [reporterName, setReporterName] = useState('');
  const [numberOfVictims, setNumberOfVictims] = useState('');
  const [reporterPhoneNumber, setReporterPhoneNumber] = useState('');
  const [sttReportText, setSttReportText] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  const [isSupportOpen, setIsSupportOpen] = useState(false);

  useEffect(() => {
    if (!report) return;
    setReporterName(report.reporter_name ?? '');
    setNumberOfVictims(String(report.number_of_victims ?? ''));
    setReporterPhoneNumber(report.reporter_phone_number ?? '');
    setSttReportText(report.stt_report_text ?? '');
    const hasCoordinates = report.latitude != null && report.longitude != null;
    setIncidentLocation(
      report.incident_location ?? (hasCoordinates ? `${report.latitude}, ${report.longitude}` : '')
    );
    setLatitude(report.latitude != null ? String(report.latitude) : '');
    setLongitude(report.longitude != null ? String(report.longitude) : '');
  }, [report]);

  const handleDecide = (action: 'dispatch' | 'reject', reject_reason?: string) => {
    decide(
      {
        action,
        reject_reason,
        report_detail: {
          reporter_name: reporterName || null,
          number_of_victims: numberOfVictims ? Number(numberOfVictims) : 1,
          reporter_phone_number: reporterPhoneNumber || null,
          incident_location: incidentLocation || null,
          latitude: latitude ? Number(latitude) : null,
          longitude: longitude ? Number(longitude) : null,
        },
      },
      {
        onSuccess: (res) => {
          if (res?.data?.conflict) {
            showToast({
              type: 'error',
              title: t('psc>msg>already decided'),
              message: t('psc>msg>already decided detail'),
            });
            return;
          }
          if (action === 'reject') {
            setModalConfirm(false);
            showToast({
              type: 'success',
              title: t('psc>msg>report rejected'),
              message: t('psc>msg>report rejected detail'),
            });
            navigate('/dashboard/psc-dashboard');
            return;
          }
          showToast({
            type: 'info',
            title: t('psc>msg>report registered'),
            message: t('psc>msg>report registered detail'),
          });
          navigate(`/dashboard/ambulance-dashboard?reportId=${reportId}`);
        },
        onError: (error: unknown) => {
          showToast({
            type: 'error',
            title: t('psc>msg>report rejected'),
            message: (error as Error)?.message ?? t('psc>msg>failed record decision'),
          });
        },
      }
    );
  };

  const handleRequestLocation = () => {
    requestLocation(
      {},
      {
        onSuccess: (res) => {
          showToast({
            type: 'success',
            title: t('psc>msg>location requested'),
            message: res?.data
              ? t('psc>msg>whatsapp sent to', { phone: res.data.phone_number_masked })
              : t('psc>msg>location request sent'),
          });
        },
        onError: (error: unknown) => {
          showToast({
            type: 'error',
            title: t('psc>msg>request failed'),
            message: (error as Error)?.message ?? t('psc>msg>failed send location request'),
          });
        },
      }
    );
  };

  const handlePasteCoordinates = async () => {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      showToast({
        type: 'error',
        title: t('psc>msg>paste failed'),
        message: t('psc>msg>could not read clipboard'),
      });
      return;
    }

    const parts = text.split(',').map((part) => part.trim());
    const lat = parts[0] ? Number(parts[0]) : Number.NaN;
    const lng = parts[1] ? Number(parts[1]) : Number.NaN;
    const isValid =
      parts.length === 2 &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    if (!isValid) {
      showToast({
        type: 'error',
        title: t('psc>msg>invalid coordinates'),
        message: t('psc>msg>invalid coordinates detail'),
      });
      return;
    }

    setLatitude(String(lat));
    setLongitude(String(lng));
  };

  if (!reportId) {
    return (
      <section className=" bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <span className="title1bold text-text-grey">{t('cmn>msg>missing report id')}</span>
        </div>
      </section>
    );
  }

  return (
    <section className="_reportRegis-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className="report-regis-screen p-3 w-full h-[calc(95vh-12px)] grid grid-cols-[1.5fr_1.5fr_1fr] gap-3 overflow-hidden">
        {/* sectionLeft */}
        <section className="w-full flex  flex-col h-full overflow-hidden rounded-[12px] border bg-db-card bg-db-border">
          <div className="p-6 flex flex-col gap-6 w-full h-full overflow-auto">
            <div className="w-full inline-flex items-center gap-3 justify-between">
              <BackLink label={t('cm > back')} onclick={() => navigate(-1)} />
              <button
                className="w-fit py-2 px-3 cursor-pointer border inline-flex gap-2 bg-white items-center border-text-blue-100 rounded-full disabled:opacity-60"
                type="button"
                onClick={() =>
                  Promise.all([refetch(), refetchChat()]).then(() =>
                    showToast({
                      type: 'success',
                      title: t('psc>msg>refreshed'),
                      message: t('psc>msg>report refreshed detail'),
                    })
                  )
                }
                disabled={isRefreshing || isChatRefreshing}
              >
                <RotateCw
                  color="#007aff"
                  className={cn((isRefreshing || isChatRefreshing) && 'animate-spin')}
                />
                <span className="title2 text-text-blue-100 capitalize">refresh</span>
              </button>
            </div>
            <div className="flex flex-row gap-1 items-center justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-text-grey uppercase dark:text-white/70 title2medium">
                  {t('cmn>master>report id')}
                </span>
                <span className="title1bold">
                  {isLoading
                    ? t('cmn>master>loading')
                    : (report?.report_code_id ?? t('psc>report>not yet assigned'))}
                </span>
              </div>
              <BubbleEvent eventTypes={status} />
            </div>

            <div className="grid grid-cols-2 w-full gap-6 items-center">
              <DetailForm
                label={t('psc>report>report by')}
                direction="col"
                inputType="text"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
              />
              <DetailForm
                label={t('psc>report>number of victims')}
                required={true}
                direction="col"
                inputType="select"
                value={numberOfVictims}
                onChange={(e) => setNumberOfVictims(e.target.value)}
                optionSelect={[
                  { value: '1', label: '1' },
                  { value: '2', label: '2' },
                  { value: '3', label: '3' },
                  { value: '4', label: '4' },
                  { value: '5', label: '5' },
                ]}
              />
            </div>

            <DetailForm
              label={t('psc>report>reporter phone number')}
              required={true}
              direction="col"
              inputType="number"
              className="relative"
              value={reporterPhoneNumber}
              onChange={(e) => setReporterPhoneNumber(e.target.value)}
            >
              <ButtonWhatsapp
                styleButton="absolute top-[45%] right-[20px]"
                label={t('psc>report>call')}
                disabled={!reporterPhoneNumber.trim()}
                onclick={() => {
                  const waNumber = toWhatsAppNumber(reporterPhoneNumber);
                  if (waNumber)
                    window.open(
                      `https://wa.me/call/+${waNumber}`,
                      '_blank',
                      'noopener,noreferrer,width=480,height=720'
                    );
                }}
              />
            </DetailForm>

            <div className="">
              <DetailForm
                label={t('psc>report>stt text (speech-to-text)')}
                direction="col"
                inputType="textarea"
                className="relative"
                styleTextArea="h-[200px]"
                value={sttReportText}
                onChange={(e) => setSttReportText(e.target.value)}
                disabled={true}
              />
            </div>

            <div className="w-full flex flex-col gap-3">
              <DetailForm
                required={true}
                label={t('psc>report>incident location')}
                direction="col"
                inputType="textarea"
                styleTextArea="h-[200px]"
                className="relative"
                value={incidentLocation}
                onChange={(e) => setIncidentLocation(e.target.value)}
                labelChildren={
                  <div className="inline-flex items-center gap-3 ml-auto">
                    <ButtonWhatsapp
                      label={t('psc>report>request location')}
                      onclick={handleRequestLocation}
                      disabled={isRequestingLocation}
                      loading={isRequestingLocation}
                    />
                    <ButtonWhatsapp
                      styleButton="!border-[#007AFF] !bg-[#F3F6FE] dark:!bg-transparent/40 "
                      label={t('psc>report>open maps')}
                      img={Gmaps}
                      styleText="capitalize"
                      tooltip={true}
                      disabled={(!latitude.trim() || !longitude.trim()) && !incidentLocation.trim()}
                      onclick={() => {
                        if (latitude.trim() && longitude.trim()) {
                          window.open(
                            `https://www.google.com/maps?q=${latitude},${longitude}`,
                            '_blank'
                          );
                          return;
                        }
                        // No coordinates yet - fall back to a text search on
                        // the incident location instead of staying disabled.
                        if (incidentLocation.trim()) {
                          window.open(
                            `https://www.google.com/maps?q=${encodeURIComponent(incidentLocation)}`,
                            '_blank'
                          );
                        }
                      }}
                    />
                  </div>
                }
              ></DetailForm>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,_minmax(150px,_1fr))] gap-6 items-end ">
              <DetailForm
                label={t('report>detail>latitude')}
                direction="col"
                inputType="text"
                pattern={/^-?\d*\.?\d*$/}
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
              <DetailForm
                label={t('report>detail>longitude')}
                direction="col"
                inputType="text"
                pattern={/^-?\d*\.?\d*$/}
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />

              <div className="col-span-2 md:col-span-1 grid">
                <Button
                  bg-text-danger-300
                  className="flex-1  btn-fill-blue text-[clamp(16px,1vw,20px)] cursor-pointer w-full line-clamp-1 text-ellipsis overflow-hidden "
                  label={t('psc>report>paste coordinates')}
                  onClick={handlePasteCoordinates}
                />
              </div>
            </div>

            <div className="">
              <DetailForm
                label={t('cmn > description')}
                direction="col"
                inputType="textarea"
                className="relative"
                styleTextArea="h-[200px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-6 items-center">
              <Button
                className="flex-1  h-[64px]! btn-fill-blue-opacity text-[20px] cursor-pointer "
                type="button"
                onClick={() => navigate(`/dashboard/details-dashboard?reportId=${reportId}`)}
              >
                <CircleAlert className="!size-[24px]" />
                {t('psc>report>report details')}
              </Button>
              <Button
                onClick={() => setIsSupportOpen(true)}
                className="flex-1  h-[64px]! btn-fill-blue-opacity text-[20px] cursor-pointer "
                type="button"
              >
                <Phone className="!size-[24px]" />
                {t('psc>report>call support')}
              </Button>
            </div>
          </div>
        </section>
        {/* sectionLeft */}

        {/* sectionCenter */}
        <section className="w-full flex  flex-col h-full overflow-hidden rounded-[12px] border bg-db-card bg-db-border">
          <div className="w-full h-full overflow-auto flex flex-col gap-6 p-6">
            <span className="text text-db-text-grey uppercase  title2medium">
              {t('psc>report>media file')}
            </span>
            <div className=" w-full ">
              <ImageGallery images={images} />
            </div>
            <div className="gap-3 flex flex-col">
              <span className="title2medium text-db-grey">
                {t('psc>report>incoming calls')} ({incomingCalls.length})
              </span>
              <div className="flex flex-col gap-3">
                {incomingCalls.map((audio) => (
                  <AudioPlayerCard
                    key={audio.id}
                    title={audio.title}
                    audioUrl={audio.audioUrl}
                    isPlaying={playingIncoming === audio.id}
                    onPlay={() =>
                      setPlayingIncoming((prev) => (prev === audio.id ? null : audio.id))
                    }
                  />
                ))}
              </div>
            </div>
            <div className="gap-3 flex flex-col">
              <span className="title2medium text-db-grey">
                {t('psc>report>voice notes')} ({voiceNotes.length})
              </span>
              <div className="flex flex-col gap-3">
                {voiceNotes.map((audio) => (
                  <AudioPlayerCard
                    key={audio.id}
                    title={audio.title}
                    audioUrl={audio.audioUrl}
                    isPlaying={playing === audio.id}
                    onPlay={() => setPlaying((prev) => (prev === audio.id ? null : audio.id))}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* sectionCenter */}

        {/* sectionRight */}
        <section className="w-full flex! gap-3  flex-col h-full overflow-hidden rounded-[12px] ">
          <div className="w-full flex h-full flex-col gap-2 h-fit p-6 overflow-auto border bg-db-card bg-db-border">
            <div className="inline-flex gap-2  items-center mb-2">
              <span className="capitalize title1bold">
                {reporterName || t('psc>report>reporter')}
              </span>
            </div>
            {chats.map((chat) => (
              <ChatBubble
                key={chat.id}
                message={chat.message}
                image={chat.image}
                video={chat.video}
                audio={chat.audio}
                caption={chat.caption}
                time={chat.time}
                isOwn={chat.isOwn}
                isProcessing={chat.isProcessing}
              />
            ))}
          </div>
          <div className="w-full flex flex-col gap-3 h-fit">
            <Button
              onClick={() => setModalRegis(true)}
              disabled={isDeciding || status !== 'incoming'}
              variant="default"
              label={t('psc>report>register')}
              customButton=""
              className="flex-1 btn-fill-blue uppercase text-[20px]"
            />
            <Button
              variant="default"
              onClick={() => setModalConfirm(true)}
              disabled={isDeciding || status !== 'incoming'}
              label={t('psc>report>reject')}
              customButton=""
              className="flex-1 bg-text-danger-300 uppercase text-text-danger-500 text-[20px] h-[45px] hover:bg-[#FF3B30]/30! cursor-pointer"
            />
          </div>
        </section>
        {/* sectionRight */}
      </div>
      <ModalConfirmation
        isOpen={modalConfirm}
        onClose={() => setModalConfirm(false)}
        onReject={(reason) => handleDecide('reject', reason)}
      />
      <ModalRegister
        isOpen={modalRegis}
        timesTamp={`${formatDateToDDMonYYYY(new Date())} · ${formatTimeOnly(new Date())}`}
        onClose={() => setModalRegis(false)}
        onRegister={() => {
          setModalRegis(false);
          handleDecide('dispatch');
        }}
      />

      <ModalSupport
        isOpen={isSupportOpen}
        onClose={() => setIsSupportOpen(false)}
        contacts={supportContacts}
        onCall={(phone) => {
          const waNumber = toWhatsAppNumber(phone);
          if (waNumber)
            window.open(
              `https://wa.me/call/+${waNumber}`,
              '_blank',
              'noopener,noreferrer,width=480,height=720'
            );
        }}
      />
    </section>
  );
};

export default ReportRegistration;
